import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { useMobileSidebar } from './Sidebar';
import { cn } from '@/lib/cn';
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  XMarkIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  EnvelopeIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { Menu } from '@headlessui/react';
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import api from '@/lib/api';
import NotificationBell from '@/components/NotificationBell';

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'nl', label: 'NL' },
  { code: 'es', label: 'ES' },
];

// ── Breadcrumb map ────────────────────────────────────────────
const ROUTE_LABELS: Record<string, string> = {
  '/': 'Dashboard',
  '/invoices': 'Invoices',
  '/invoices/new': 'New Invoice',
  '/quotes': 'Quotes',
  '/quotes/new': 'New Quote',
  '/expenses': 'Expenses',
  '/expenses/new': 'New Expense',
  '/clients': 'Clients',
  '/bank': 'Bank',
  '/documents': 'Documents',
  '/emails': 'Emails',
  '/reports/vat': 'VAT Reports',
  '/subscription': 'Subscription',
  '/settings': 'Settings',
};

function buildBreadcrumbs(pathname: string) {
  if (pathname === '/') return [{ path: '/', label: 'Dashboard' }];

  const parts = pathname.split('/').filter(Boolean);
  const crumbs: { path: string; label: string }[] = [
    { path: '/', label: 'Dashboard' },
  ];

  let built = '';
  for (const part of parts) {
    built += `/${part}`;
    const label = ROUTE_LABELS[built] ?? part.charAt(0).toUpperCase() + part.slice(1);
    crumbs.push({ path: built, label });
  }

  return crumbs;
}

// ── Search result types ───────────────────────────────────────
interface SearchResult {
  id: string;
  type: 'client' | 'invoice' | 'quote' | 'expense';
  title: string;
  subtitle: string;
  path: string;
}

// ── Component ─────────────────────────────────────────────────
export default function Navbar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const setMobileOpen = useMobileSidebar((s) => s.setOpen);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const searchPanelRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const crumbs = useMemo(() => buildBreadcrumbs(pathname), [pathname]);

  // Cmd+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => searchRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Close search panel on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchPanelRef.current && !searchPanelRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
      }
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced global search
  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const results: SearchResult[] = [];
      const [clients, invoices, quotes, expenses] = await Promise.all([
        api.get('/clients', { params: { search: q, limit: 5 } }).catch(() => ({ data: { data: [] } })),
        api.get('/invoices', { params: { search: q, limit: 5 } }).catch(() => ({ data: { data: [] } })),
        api.get('/quotes', { params: { search: q, limit: 5 } }).catch(() => ({ data: { data: [] } })),
        api.get('/expenses', { params: { search: q, limit: 5 } }).catch(() => ({ data: { data: [] } })),
      ]);

      for (const c of (clients.data?.data || [])) {
        results.push({
          id: c.id,
          type: 'client',
          title: c.name,
          subtitle: c.email || c.vatNumber || '',
          path: `/clients/${c.id}`,
        });
      }
      for (const inv of (invoices.data?.data || [])) {
        results.push({
          id: inv.id,
          type: 'invoice',
          title: inv.invoiceNumber || 'Draft',
          subtitle: `${inv.client?.name || ''} — €${Number(inv.totalAmount || 0).toFixed(2)}`,
          path: `/invoices/${inv.id}`,
        });
      }
      for (const q2 of (quotes.data?.data || [])) {
        results.push({
          id: q2.id,
          type: 'quote',
          title: q2.quoteNumber || 'Draft',
          subtitle: `${q2.client?.name || ''} — €${Number(q2.totalAmount || 0).toFixed(2)}`,
          path: `/quotes/${q2.id}`,
        });
      }
      for (const exp of (expenses.data?.data || [])) {
        results.push({
          id: exp.id,
          type: 'expense',
          title: exp.description || exp.supplier || 'Expense',
          subtitle: `€${Number(exp.totalAmount || 0).toFixed(2)}`,
          path: `/expenses`,
        });
      }
      setSearchResults(results);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(value), 300);
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(result.path);
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('ledgora-lang', lang);
    setLangMenuOpen(false);
  };

  const TYPE_ICONS: Record<string, string> = {
    client: '👤',
    invoice: '📄',
    quote: '📋',
    expense: '💰',
  };

  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : 'U';

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center border-b border-gray-200 bg-white px-4 lg:px-6 gap-2">
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        className={cn(
          'md:hidden flex h-9 w-9 items-center justify-center rounded-lg',
          'text-gray-500 hover:text-gray-900 hover:bg-gray-100',
          'transition-all duration-200 ease-in-out',
        )}
        aria-label="Open sidebar"
      >
        <Bars3Icon className="h-5 w-5" />
      </button>

      {/* Breadcrumbs - desktop */}
      <nav className="hidden md:flex items-center gap-1 text-sm" aria-label="Breadcrumb">
        {crumbs.map((crumb, i) => (
          <span key={crumb.path} className="flex items-center gap-1">
            {i > 0 && (
              <ChevronRightIcon className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
            )}
            <span
              className={cn(
                i === crumbs.length - 1 ? 'text-gray-900 font-medium' : 'text-gray-500',
              )}
            >
              {crumb.label}
            </span>
          </span>
        ))}
      </nav>
      {/* Mobile: current page */}
      <span className="md:hidden text-sm font-medium text-gray-900 truncate">
        {crumbs[crumbs.length - 1]?.label}
      </span>

      <div className="flex-1" />

      {/* ── Global Search ── */}
      <div className="relative" ref={searchPanelRef}>
        <button
          onClick={() => {
            setSearchOpen(true);
            setTimeout(() => searchRef.current?.focus(), 50);
          }}
          className={cn(
            'flex items-center gap-2 h-9 rounded-lg border bg-gray-50 px-3 text-sm text-gray-400',
            'hover:bg-white hover:border-gray-300 transition-all cursor-pointer',
            searchOpen ? 'hidden' : 'hidden sm:flex',
          )}
        >
          <MagnifyingGlassIcon className="h-4 w-4" />
          <span>{t('common.search')} ⌘K</span>
        </button>
        {/* Mobile search icon */}
        <button
          onClick={() => {
            setSearchOpen(true);
            setTimeout(() => searchRef.current?.focus(), 50);
          }}
          className={cn('sm:hidden flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100', searchOpen && 'hidden')}
        >
          <MagnifyingGlassIcon className="h-5 w-5" />
        </button>

        {/* Search overlay */}
        {searchOpen && (
          <div className="fixed inset-0 sm:absolute sm:inset-auto sm:right-0 sm:top-0 sm:w-[420px] z-50">
            {/* Mobile backdrop */}
            <div className="fixed inset-0 bg-black/20 sm:hidden" onClick={() => { setSearchOpen(false); setSearchQuery(''); setSearchResults([]); }} />
            <div className="relative bg-white sm:rounded-lg sm:shadow-xl sm:border sm:border-gray-200 h-full sm:h-auto sm:max-h-[70vh] flex flex-col">
              <div className="flex items-center gap-2 px-4 h-14 border-b border-gray-100">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 shrink-0" />
                <input
                  ref={searchRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder={`${t('common.search')}...`}
                  className="flex-1 bg-transparent text-sm text-gray-900 placeholder-gray-400 outline-none"
                  autoFocus
                />
                <button onClick={() => { setSearchOpen(false); setSearchQuery(''); setSearchResults([]); }} className="p-1 rounded text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {searching && (
                  <div className="px-4 py-8 text-center text-sm text-gray-400">{t('common.loading')}</div>
                )}
                {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                  <div className="px-4 py-8 text-center text-sm text-gray-400">{t('common.noResults')}</div>
                )}
                {!searching && searchResults.length > 0 && (
                  <ul className="py-2">
                    {searchResults.map((r) => (
                      <li key={`${r.type}-${r.id}`}>
                        <button
                          onClick={() => handleResultClick(r)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition-colors"
                        >
                          <span className="text-lg shrink-0">{TYPE_ICONS[r.type]}</span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate">{r.title}</div>
                            <div className="text-xs text-gray-500 truncate">{r.subtitle}</div>
                          </div>
                          <span className="text-[10px] uppercase font-medium text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                            {r.type}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {!searching && searchQuery.length < 2 && (
                  <div className="px-4 py-8 text-center text-xs text-gray-400">
                    {t('common.search')}...
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Language switcher */}
      <div className="relative" ref={langRef}>
        <button
          onClick={() => setLangMenuOpen(!langMenuOpen)}
          className={cn(
            'flex h-9 items-center justify-center rounded-lg px-2.5',
            'text-xs font-semibold uppercase',
            'text-gray-600 hover:text-gray-900 hover:bg-gray-100',
            'transition-all duration-200 ease-in-out border border-gray-200',
          )}
        >
          {i18n.language.toUpperCase()}
        </button>
        {langMenuOpen && (
          <div className="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={cn(
                  'block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors',
                  i18n.language === lang.code ? 'text-indigo-600 font-medium' : 'text-gray-700',
                )}
              >
                {lang.label === 'EN' ? 'English' : lang.label === 'FR' ? 'Français' : lang.label === 'NL' ? 'Nederlands' : 'Español'}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Notifications */}
      <NotificationBell />

      {/* User menu */}
      <Menu as="div" className="relative">
        <Menu.Button className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <span className="hidden sm:block text-sm font-medium text-gray-700 truncate max-w-[140px]">
            {user ? `${user.firstName} ${user.lastName}` : ''}
          </span>
          <div
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full',
              'bg-indigo-600 text-white text-xs font-semibold',
              'ring-2 ring-white shadow-sm',
            )}
            aria-label={`User: ${user?.firstName ?? 'User'}`}
          >
            {initials}
          </div>
        </Menu.Button>
        <Menu.Items className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50">
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={() => navigate('/settings')}
                className={`flex items-center gap-2.5 w-full px-4 py-2 text-sm ${active ? 'bg-gray-50' : ''} text-gray-700`}
              >
                <Cog6ToothIcon className="w-4 h-4 text-gray-400" />
                {t('nav.settings')}
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={() => navigate('/subscription')}
                className={`flex items-center gap-2.5 w-full px-4 py-2 text-sm ${active ? 'bg-gray-50' : ''} text-gray-700`}
              >
                <CreditCardIcon className="w-4 h-4 text-gray-400" />
                {t('nav.subscription')}
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={() => navigate('/emails')}
                className={`flex items-center gap-2.5 w-full px-4 py-2 text-sm ${active ? 'bg-gray-50' : ''} text-gray-700`}
              >
                <EnvelopeIcon className="w-4 h-4 text-gray-400" />
                {t('nav.emails')}
              </button>
            )}
          </Menu.Item>
          <div className="my-1 h-px bg-gray-100" />
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={handleLogout}
                className={`flex items-center gap-2.5 w-full px-4 py-2 text-sm ${active ? 'bg-red-50' : ''} text-red-600`}
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                {t('nav.logout')}
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Menu>
    </header>
  );
}
